import { supabase } from '@/integrations/supabase/client';

/**
 * Fetcha il livello finitura (Q1-Q4) + componenti BOM per l'org corrente,
 * calcola il costo finitura €/m² (materiali netti + manodopera) e ritorna
 * anche lo snapshot della BOM da salvare su estimate_stratigraphies.
 *
 * F7.7. Estratto da useCreateEstimateStratigraphy per essere riusato anche
 * dalla modifica inline del livello finitura nella tabella preventivo.
 *
 * FACCE DA FINIRE. I valori configurati in Impostazioni (minuti e kg di stucco
 * per m²) sono riferiti a una parete DIVISORIA, cioe' finita su ENTRAMBE le
 * facce. Una controparete o un controsoffitto hanno una faccia sola e vanno
 * quindi contati a meta'.
 *
 * Prima non si guardava affatto la tipologia: qualunque stratigrafia prendeva
 * lo stesso costo. Su Drycore erano ~186.000 € di finitura addebitata in piu'
 * su 19 righe di preventivo (39.933 m² di contropareti). Il numero di facce lo
 * ricava il chiamante da `detectTypology`.
 */
export async function fetchFinishCostSnapshot(
  organizationId: string,
  finishLevelCode: string,
  /**
   * Facce da finire della stratigrafia: 2 per una parete divisoria, 1 per una
   * controparete o un controsoffitto. Default 2 = comportamento storico.
   */
  finishedFaces: 1 | 2 = 2,
): Promise<{
  finishCostPerSqm: number;
  finishLaborMinPerSqm: number;
  finishComponentsSnapshot: Array<Record<string, unknown>>;
} | null> {
  // 1) Costo orario
  const { data: settingData } = await supabase
    .from('configurator_settings')
    .select('value')
    .eq('key', 'cost_per_hour')
    .maybeSingle();
  const hourlyRate = parseFloat(settingData?.value ?? '30') || 30;

  // 2) Livello finitura
  const { data: level, error: levelError } = await supabase
    .from('finish_levels')
    .select('id, code, labor_minutes_per_sqm')
    .eq('organization_id', organizationId)
    .eq('code', finishLevelCode)
    .maybeSingle();
  if (levelError || !level) {
    console.warn('[fetchFinishCostSnapshot] livello non trovato:', finishLevelCode);
    return null;
  }

  // 3) Componenti BOM con materiali joined
  const { data: components } = await supabase
    .from('finish_level_components')
    .select(`
      id,
      quantity_per_sqm,
      notes,
      material:materials!finish_level_components_material_id_fkey (
        id, name, code, unit, unit_price, supplier, category, box_pieces, waste_percentage
      )
    `)
    .eq('finish_level_id', level.id);

  // 🔥 F20.2 — Override unit_price col NETTO da materials_with_pricing.
  // Il join via FK carica `materials` raw dove unit_price = list_price
  // (vedi commento in useStratigraphy.ts / useBulkUpdateEstimateStratigraphyPrices).
  type FinishCompWithMat = { material?: { id?: string; unit_price?: number | null } | null };
  const compsArr = (components ?? []) as FinishCompWithMat[];
  const finishMatIds = compsArr
    .map((c) => c.material?.id)
    .filter((x: string | undefined): x is string => !!x);
  const uniqueFinishMatIds = Array.from(new Set(finishMatIds));
  if (uniqueFinishMatIds.length > 0) {
    const { data: pricing } = await supabase
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .from('materials_with_pricing' as any)
      .select('id, unit_price')
      .in('id', uniqueFinishMatIds);
    const priceMap = new Map<string, number>();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    for (const p of (pricing ?? []) as any[]) {
      priceMap.set(p.id, Number(p.unit_price ?? 0));
    }
    for (const c of compsArr) {
      if (c.material?.id && priceMap.has(c.material.id)) {
        c.material.unit_price = priceMap.get(c.material.id);
      }
    }
  }

  // 4) Calcolo costo materiali + snapshot BOM
  // Le quantita' configurate valgono per 2 facce: su una faccia sola si dimezza
  // sia lo stucco sia il tempo. Il fattore entra QUI, cosi' lo snapshot salvato
  // sulla riga e' gia' quello giusto per quella parete e tutto cio' che sta a
  // valle (bulk update, Riepilogo Materiali, RDA) lo eredita senza saperne nulla.
  const faceFactor = finishedFaces / 2;
  let materialsCost = 0;
  const snapshot: Array<Record<string, unknown>> = [];
  for (const c of compsArr) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const mat = c.material as any;
    if (!mat) continue;
    const unitPrice = Number(mat.unit_price ?? 0);
    // La confezione vale per QUALSIASI unita`, non solo per la "scatola" — deve
    // restare identica a `computeFinishLevelCost`, che e` la formula mostrata in
    // Impostazioni mentre questa e` quella che poi CONGELA il costo sulla riga.
    // Il vincolo `unit === 'scatola'` lasciava fuori gli stucchi a kg (FASSAJOINT
    // 2H: 8,839 EUR il sacco da 5 kg), contati cinque volte tanto.
    const box = Number(mat.box_pieces ?? 0);
    const pricePerUsage = box > 0 ? unitPrice / box : unitPrice;
    const qty = Math.round(Number(c.quantity_per_sqm ?? 0) * faceFactor * 10000) / 10000;
    const rowCost = qty * pricePerUsage;
    materialsCost += rowCost;
    snapshot.push({
      material_id: mat.id,
      material_name: mat.name,
      material_code: mat.code,
      material_unit: mat.unit,
      material_supplier: mat.supplier,
      unit_price: unitPrice,
      box_pieces: mat.box_pieces,
      quantity_per_sqm: qty,
      cost_per_sqm: Math.round(rowCost * 10000) / 10000,
      waste_percentage: mat.waste_percentage ?? null,
      notes: c.notes,
    });
  }

  const laborMin = Math.round((Number(level.labor_minutes_per_sqm) || 0) * faceFactor * 100) / 100;
  const laborCost = (laborMin * hourlyRate) / 60;
  const totalCost = materialsCost + laborCost;

  return {
    finishCostPerSqm: Math.round(totalCost * 10000) / 10000,
    finishLaborMinPerSqm: laborMin,
    finishComponentsSnapshot: snapshot,
  };
}
